import { Component, type ReactNode } from "react";

/**
 * OWNEXP Slice 12/15 — Flächen-Fehlergrenze: Ein Render-Fehler in EINEM
 * Control-Plane-Bereich (z. B. überraschendes Read-Model-Shape) degradiert
 * nur diesen Bereich — nie die ganze Leitstelle (White-Screen-Gefahr).
 * Der Fehler wird als strukturierter Hinweis sichtbar (UI-Observability:
 * JS-Fehler fließen später ins Fehler-Reporting), keine Fake-Daten.
 */
interface Props {
  label: string;
  children: ReactNode;
}

interface State {
  fehler: Error | null;
}

export class PanelErrorBoundary extends Component<Props, State> {
  state: State = { fehler: null };

  static getDerivedStateFromError(fehler: Error): State {
    return { fehler };
  }

  componentDidCatch(fehler: Error): void {
    // UI-Observability-Haken: strukturiertes Fehler-Ereignis (window-Fehler-
    // Kanal, späterer Ingest als Finding/Incident möglich).
    try {
      window.dispatchEvent(new CustomEvent("simo:ui-panel-error", {
        detail: { panel: "control-plane", label: this.props.label, message: fehler.message, at: new Date().toISOString() },
      }));
    } catch {
      // Beobachtbarkeit darf die Render-Fehlergrenze nie selbst werfen.
    }
  }

  render(): ReactNode {
    if (this.state.fehler) {
      return (
        <div
          className="card border border-amber-300/40 p-4"
          role="alert"
          data-testid="panel-error-boundary"
          aria-label={`Bereich ${this.props.label} gestört`}
        >
          <h2 className="text-sm font-semibold text-amber-200">Bereich „{this.props.label}" ist gerade gestört</h2>
          <p className="mt-1 text-xs text-jarvis-muted">
            Dieser Bereich konnte seine Daten nicht sicher darstellen (Ursache: {this.state.fehler.message}). Die
            anderen Bereiche der Control Plane arbeiten weiter — hier werden bewusst keine Ersatzdaten erfunden.
          </p>
          <button type="button" className="btn mt-2 px-3 py-1.5 text-xs" onClick={() => this.setState({ fehler: null })}>
            Erneut versuchen
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
